/**
 * Walking a page of operation rows, one detail at a time.
 *
 * The admin view model over `@shared-ui/item-nav` — the teacher app builds its
 * own over the same view. Everything app-specific is here: which route a row
 * lives at, and how the walk is threaded from the list.
 *
 * **Bounded by the page you came from.** The lists are server-paged at 20, so
 * the walk is those 20 rows; Next is absent on the last of them rather than
 * fetching the next page. The label says which page, so "Run 20 of 20" never
 * reads as "the end of the results".
 */
import { useNavigate } from 'react-router-dom';
import { ItemNavView } from '@shared-ui/item-nav';

/** Threaded through router state when a list row is opened. */
export interface OperationNav {
    /** Ids of the rows on the page this walk came from, in the order shown. */
    ids: string[];
    /** Where in `ids` the open row sits. */
    index: number;
    /** `/submissions`, `/hints`, … — the detail route these ids hang off. */
    basePath: string;
    /** The list's page number, for the label. Absent on an unpaged list. */
    page?: number;
    /** The search string the list was showing, so the way back survives. */
    backSearch?: string;
    backTo?: string;
    backLabel?: string;
}

/** What a detail page passes on when it navigates anywhere that should be able
 *  to come back into the same walk. */
export function operationNavState(nav: OperationNav, index: number) {
    return {
        nav: { ...nav, index },
        backSearch: nav.backSearch,
        backTo: nav.backTo,
        backLabel: nav.backLabel,
    };
}

export function OperationNavStrip({ nav, noun = 'Run' }: { nav: OperationNav; noun?: string }) {
    const navigate = useNavigate();
    const go = (index: number) => {
        const id = nav.ids[index];
        if (!id) return;
        navigate(`${nav.basePath}/${id}`, { state: operationNavState(nav, index) });
    };

    const where = nav.page && nav.page > 1 ? ` (page ${nav.page})` : '';

    return (
        <ItemNavView
            label={`${noun} ${nav.index + 1} of ${nav.ids.length}${where}`}
            onPrev={nav.index > 0 ? () => go(nav.index - 1) : undefined}
            onNext={nav.index < nav.ids.length - 1 ? () => go(nav.index + 1) : undefined}
            prevTitle={`Previous ${noun.toLowerCase()}`}
            nextTitle={`Next ${noun.toLowerCase()}`}
        />
    );
}

/**
 * Open a list row, carrying the rest of the page with it.
 *
 * The rows go in as they are RENDERED, so the walk follows the sort and filter
 * the reader is looking at rather than some canonical order they never chose.
 *
 * `backSearch` and friends ride along untouched: stepping to the next run must
 * not cost the way back to the list, which is the state the detail page reads
 * for its own back link.
 */
export function useOpenOperationRow() {
    const navigate = useNavigate();
    return <T extends { id: string }>(
        rows: readonly T[],
        row: T,
        basePath: string,
        extra?: { page?: number; backSearch?: string; backTo?: string; backLabel?: string },
    ) => {
        const ids = rows.map(r => r.id);
        const nav: OperationNav = { ids, index: ids.indexOf(row.id), basePath, ...extra };
        navigate(`${basePath}/${row.id}`, { state: { nav, ...extra } });
    };
}
